import { useMutation, useQueryClient } from '@tanstack/react-query';

import API from 'services/api';
import FormSubmitError from 'errors/FormSubmitError';

import { useProsforaAuctionVolumes, useRfpBidHistory } from '../rfp';
import { AxiosError } from 'axios';

interface BidEntry {
  id: number;
  volume: number;
  bidPrice: number;
  downPayment: number;
  discount: number;
}

const submitBidsMutation = async (rfpId: number, bids: BidEntry[]) => {
  try {
    return (
      await API.post(
        `/v2/rfp/${rfpId}/submit-bids/`,
        bids.map((bid) => ({
          traro_element_id: bid.id,
          volume: bid.volume,
          bid_price: bid.bidPrice,
          down_payment: bid.downPayment,
          discount: bid.discount,
        })),
      )
    ).data;
  } catch (e) {
    if (e instanceof AxiosError && e.response) {
      throw new FormSubmitError(e.response.data.error);
    } else {
      throw new FormSubmitError('An error occurred while sending request to the server. Please try again.');
    }
  }
};

const useSubmitBidsMutation = (rfpId: number) => {
  const queryClient = useQueryClient();

  return useMutation<void, FormSubmitError, BidEntry[]>((params) => submitBidsMutation(rfpId, params), {
    onSuccess: async () => {
      for (const value of [useRfpBidHistory.getQueryKey(rfpId), useProsforaAuctionVolumes.getQueryKey(rfpId)]) {
        await queryClient.invalidateQueries({ queryKey: value });
      }
    },
  });
};

useSubmitBidsMutation.queries = {
  submitBidsMutation,
} as const;

export default useSubmitBidsMutation;
